import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === "en" ? "fr" : "en");
  };

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/vendors", label: t("nav.vendors") },
    { to: "/riders", label: t("nav.riders") },
    { to: "/waitlist", label: t("nav.waitlist") },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/98 backdrop-blur-md border-b border-background-200">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://storage.readdy-site.link/project_files/117133a6-3dd8-43d6-a464-18afa34af3c0/9887b7f2-ccf6-45c1-a630-cdac611c7766_compressed_mayouk-logo.webp"
              alt="Mayouk"
              className="h-10 w-10 md:h-12 md:w-12 object-contain"
            />
            <span className="font-heading text-xl md:text-2xl font-bold text-primary-600">
              Mayouk
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(link.to)
                    ? "text-accent-500"
                    : "text-foreground-900 hover:text-accent-500"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground-700 hover:text-accent-500 transition-all px-3 py-1.5 rounded-full border border-background-300 hover:border-accent-400 hover:bg-accent-50 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line"></i>
              </div>
              <span>{i18n.language === "fr" ? "EN" : "FR"}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 md:hidden">
            <button
              onClick={toggleLanguage}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-sm font-semibold text-foreground-700 hover:text-accent-500 transition-colors cursor-pointer"
            >
              <span className="text-xs">{i18n.language === "fr" ? "EN" : "FR"}</span>
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200 animate-fade-in">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? "text-accent-500 bg-accent-50"
                    : "text-foreground-900 hover:bg-accent-50 hover:text-accent-500"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}